import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, ArrowRight, Check, CheckCircle2, AlertCircle, Bot, Loader2, Rocket, Trash2, Plus, Send,
  Target, Package, Users, TrendingUp, HelpCircle, Shield, Mic, FileText,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { cn } from '../../lib/utils';
import { errorText } from '../../lib/errorText';
import { ListField, FaqField, KnowledgeBaseManager } from './CampaignFields';

const API = process.env.REACT_APP_BACKEND_URL;

export const WIZARD_STEPS = [
  { key: 'general', label: 'General', Icon: Target },
  { key: 'products', label: 'Productos', Icon: Package },
  { key: 'customer', label: 'Cliente ideal', Icon: Users },
  { key: 'strategy', label: 'Estrategia', Icon: TrendingUp },
  { key: 'faq', label: 'FAQ', Icon: HelpCircle },
  { key: 'policies', label: 'Políticas', Icon: Shield },
  { key: 'tone', label: 'Tono Noah', Icon: Mic },
  { key: 'instructions', label: 'Instrucciones', Icon: FileText },
  { key: 'launch', label: 'Lanzar', Icon: Rocket },
];

const COLORS = ['#002d2c', '#559975', '#77bd9b', '#e6fff1', '#f4eb9f', '#c48a4b', '#85562e', '#1a4034'];
const CHANNELS = ['whatsapp', 'sms', 'messenger', 'instagram', 'web', 'email'];
const ACCENTS = ['neutro', 'mexicano', 'argentino', 'espanol', 'colombiano-paisa', 'colombiano-costeño', 'colombiano-bogotano', 'chileno', 'peruano', 'venezolano'];
const NOAH_MODES = [
  { value: 'auto', title: 'Automático', desc: 'Noah responde solo, actualiza el CRM y escala a un humano cuando no sabe.' },
  { value: 'assist', title: 'Copiloto', desc: 'Noah redacta la respuesta y tu equipo la aprueba o edita antes de enviar.' },
  { value: 'manual', title: 'Manual', desc: 'Solo humanos responden. Noah no interviene en esta campaña.' },
];

const EMPTY = {
  name: '', description: '', objective: '', focus_type: 'ventas', color: '#559975', group_id: '', channels: [], active: true,
  target_audience: '', customer_profile: { description: '', pains: '', triggers: '', objections: '', channels: '' },
  value_proposition: '', unique_selling_points: [], pricing_strategy: '', promotions: '', sales_process_steps: [],
  lead_qualification_questions: [], common_objections: [], competitors: '', success_stories: '',
  faqs: [],
  policies: { shipping: '', returns: '', warranty: '', payment: '', privacy: '', other: '' }, payment_methods: [], working_hours: '', legal_disclaimers: '',
  accent: 'neutro', tone_of_voice: '', brand_name: '', key_messages: [], forbidden_topics: [], languages: ['es'],
  ai_instructions: '', escalation_criteria: '', contact_info: '', noah_mode: 'auto',
};

const STEP_FIELDS = {
  general: ['name', 'description', 'objective', 'focus_type', 'color', 'group_id', 'channels', 'active'],
  products: [],
  customer: ['target_audience', 'customer_profile'],
  strategy: ['value_proposition', 'unique_selling_points', 'pricing_strategy', 'promotions', 'sales_process_steps', 'lead_qualification_questions', 'common_objections', 'competitors', 'success_stories'],
  faq: ['faqs'],
  policies: ['policies', 'payment_methods', 'working_hours', 'legal_disclaimers'],
  tone: ['accent', 'tone_of_voice', 'brand_name', 'key_messages', 'forbidden_topics', 'languages'],
  instructions: ['ai_instructions', 'escalation_criteria', 'contact_info', 'noah_mode'],
  launch: ['active', 'noah_mode'],
};

const fromCampaign = (c) => {
  if (!c) return { ...EMPTY };
  const out = { ...EMPTY };
  Object.keys(EMPTY).forEach((k) => {
    if (c[k] === undefined || c[k] === null) return;
    out[k] = typeof EMPTY[k] === 'object' && !Array.isArray(EMPTY[k]) ? { ...EMPTY[k], ...(c[k] || {}) } : c[k];
  });
  out.group_id = c.group_id || '';
  return out;
};

export const CampaignWizard = ({ open, campaign, groups = [], onClose, onSaved }) => {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(fromCampaign(campaign));
  const [campaignId, setCampaignId] = useState(campaign?.id || null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setForm(fromCampaign(campaign)); setCampaignId(campaign?.id || null); setStep(0); }
  }, [open, campaign]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setNested = (key, sub, value) => setForm((f) => ({ ...f, [key]: { ...(f[key] || {}), [sub]: value } }));
  const current = WIZARD_STEPS[step];

  const saveStep = async () => {
    const fields = STEP_FIELDS[current.key];
    const payload = {};
    fields.forEach((k) => { payload[k] = k === 'group_id' ? (form.group_id || null) : form[k]; });
    setSaving(true);
    try {
      let id = campaignId;
      if (!id) {
        if (!form.name.trim()) { toast.error('Ponle un nombre a la campaña'); return false; }
        const { data } = await axios.post(`${API}/api/campaigns`, { ...payload, group_id: form.group_id || null });
        id = data.id; setCampaignId(id);
        toast.success('Campaña creada · sigamos configurando a Noah');
      } else if (fields.length) {
        await axios.put(`${API}/api/campaigns/${id}/wizard/${current.key}`, payload);
      }
      onSaved?.();
      return true;
    } catch (e) {
      toast.error(errorText(e, 'No se pudo guardar este paso'));
      return false;
    } finally { setSaving(false); }
  };

  const next = async () => { if (await saveStep()) setStep((s) => Math.min(s + 1, WIZARD_STEPS.length - 1)); };
  const back = () => setStep((s) => Math.max(s - 1, 0));
  const jump = async (i) => { if (i === step) return; if (i < step || campaignId) { if (await saveStep()) setStep(i); } };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent aria-describedby={undefined} className="max-w-3xl max-h-[92vh] overflow-hidden p-0 flex flex-col" data-testid="campaign-wizard">
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-border">
          <DialogTitle className="flex items-center gap-2">
            <Bot className="w-5 h-5 text-brand-green" />
            {campaignId ? `Configurar: ${form.name || 'campaña'}` : 'Nueva campaña · Asistente'}
          </DialogTitle>
          <StepRail step={step} onJump={jump} campaignId={campaignId} />
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          {current.key === 'general' && <StepGeneral form={form} set={set} groups={groups} />}
          {current.key === 'products' && <StepProducts campaignId={campaignId} />}
          {current.key === 'customer' && <StepCustomer form={form} set={set} setNested={setNested} />}
          {current.key === 'strategy' && <StepStrategy form={form} set={set} />}
          {current.key === 'faq' && <StepFaq form={form} set={set} campaignId={campaignId} />}
          {current.key === 'policies' && <StepPolicies form={form} set={set} setNested={setNested} />}
          {current.key === 'tone' && <StepTone form={form} set={set} />}
          {current.key === 'instructions' && <StepInstructions form={form} set={set} />}
          {current.key === 'launch' && <StepLaunch form={form} set={set} campaignId={campaignId} />}
        </div>

        <div className="px-6 py-3 border-t border-border flex items-center justify-between gap-2 bg-background/60">
          <Button variant="ghost" onClick={back} disabled={step === 0 || saving} data-testid="wizard-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Atrás
          </Button>
          <span className="text-xs text-muted-foreground">Paso {step + 1} de {WIZARD_STEPS.length}</span>
          {step < WIZARD_STEPS.length - 1 ? (
            <Button onClick={next} disabled={saving || (!campaignId && !form.name.trim())} className="bg-primary" data-testid="wizard-next-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Guardar y seguir <ArrowRight className="w-4 h-4 ml-1" /></>}
            </Button>
          ) : (
            <Button onClick={async () => { if (await saveStep()) { toast.success('Campaña lista'); onClose?.(); } }} disabled={saving} className="bg-brand-green text-white" data-testid="wizard-finish-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" /> Terminar</>}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const StepRail = ({ step, onJump, campaignId }) => (
  <div className="flex gap-1 overflow-x-auto pt-3 -mx-1 px-1 pb-1" data-testid="wizard-step-rail">
    {WIZARD_STEPS.map((s, i) => (
      <button key={s.key} type="button" onClick={() => onJump(i)} disabled={!campaignId && i > step}
        data-testid={`wizard-step-${s.key}`}
        className={cn('flex items-center gap-1.5 shrink-0 rounded-full px-3 py-1.5 text-xs transition-colors',
          i === step ? 'bg-brand-deep text-brand-butter' : i < step ? 'bg-brand-green/15 text-brand-deep dark:text-brand-green' : 'bg-muted text-muted-foreground',
          !campaignId && i > step && 'opacity-50 cursor-not-allowed')}>
        <s.Icon className="w-3.5 h-3.5" /> {s.label}
      </button>
    ))}
  </div>
);

const Section = ({ title, hint, children }) => (
  <div className="space-y-3">
    <div><h4 className="text-sm font-semibold">{title}</h4>{hint && <p className="text-xs text-muted-foreground">{hint}</p>}</div>
    {children}
  </div>
);

const StepGeneral = ({ form, set, groups }) => (
  <Section title="Lo básico" hint="Noah usa el objetivo para saber qué está intentando lograr en cada conversación.">
    <div><Label>Nombre de la campaña *</Label><Input data-testid="campaign-name-input" value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="Ej: Black Friday 2026" /></div>
    <div><Label>Descripción</Label><Textarea data-testid="campaign-desc-input" rows={2} value={form.description} onChange={(e) => set({ description: e.target.value })} placeholder="¿Qué vendes o atiendes en esta campaña?" /></div>
    <div><Label>Objetivo principal</Label><Textarea data-testid="campaign-objective-input" rows={2} value={form.objective} onChange={(e) => set({ objective: e.target.value })} placeholder="Ej: Cerrar 50 ventas del plan anual con 20% de descuento" /></div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div><Label>Enfoque de Noah</Label>
        <Select value={form.focus_type} onValueChange={(v) => set({ focus_type: v })}>
          <SelectTrigger data-testid="campaign-focus-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ventas">Ventas</SelectItem><SelectItem value="servicio_cliente">Servicio al cliente</SelectItem>
            <SelectItem value="soporte_tecnico">Soporte técnico</SelectItem><SelectItem value="pqr">PQR</SelectItem><SelectItem value="mixto">Mixto</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div><Label>Grupo</Label>
        <Select value={form.group_id || 'none'} onValueChange={(v) => set({ group_id: v === 'none' ? '' : v })}>
          <SelectTrigger data-testid="campaign-group-select"><SelectValue placeholder="Sin grupo" /></SelectTrigger>
          <SelectContent><SelectItem value="none">Sin grupo</SelectItem>{groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
    </div>
    <div><Label>Canales previstos</Label>
      <div className="flex flex-wrap gap-2 mt-1">
        {CHANNELS.map((c) => (
          <button key={c} type="button" data-testid={`campaign-channel-${c}`}
            onClick={() => set({ channels: form.channels.includes(c) ? form.channels.filter((x) => x !== c) : [...form.channels, c] })}
            className={cn('rounded-full px-3 py-1 text-xs border transition-colors', form.channels.includes(c) ? 'bg-brand-green text-white border-brand-green' : 'border-border text-muted-foreground')}>
            {c}
          </button>
        ))}
      </div>
    </div>
    <div><Label>Color</Label><div className="flex gap-2 mt-1">{COLORS.map((c) => <button key={c} type="button" data-testid={`campaign-color-${c.slice(1)}`} className={cn('w-8 h-8 rounded-full border-2 transition-transform', form.color === c ? 'border-foreground scale-110' : 'border-transparent')} style={{ backgroundColor: c }} onClick={() => set({ color: c })} />)}</div></div>
  </Section>
);

const StepProducts = ({ campaignId }) => {
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState({ name: '', description: '', price: '', stock: '' });
  const [busy, setBusy] = useState(false);
  const load = React.useCallback(async () => {
    if (!campaignId) return;
    try { const { data } = await axios.get(`${API}/api/products`, { params: { campaign_id: campaignId } }); setItems(data || []); } catch { /* silent */ }
  }, [campaignId]);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    if (!draft.name.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/api/products`, { name: draft.name.trim(), description: draft.description || draft.name, campaign_id: campaignId,
        price: draft.price ? parseFloat(draft.price) : null, stock: draft.stock ? parseInt(draft.stock, 10) : null });
      setDraft({ name: '', description: '', price: '', stock: '' }); await load(); toast.success('Producto añadido');
    } catch (e) { toast.error(errorText(e, 'No se pudo añadir')); } finally { setBusy(false); }
  };
  const remove = async (id) => { try { await axios.delete(`${API}/api/products/${id}`); await load(); } catch { toast.error('No se pudo eliminar'); } };
  return (
    <Section title="Productos y servicios de ESTA campaña" hint="Noah solo cotiza con estos precios. Si no hay productos, no inventa cifras y escala al equipo.">
      <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 items-end">
        <div className="sm:col-span-2"><Label>Nombre *</Label><Input data-testid="wizard-product-name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Plan anual" /></div>
        <div><Label>Precio</Label><Input data-testid="wizard-product-price" type="number" value={draft.price} onChange={(e) => setDraft({ ...draft, price: e.target.value })} placeholder="99" /></div>
        <div><Label>Stock</Label><Input data-testid="wizard-product-stock" type="number" value={draft.stock} onChange={(e) => setDraft({ ...draft, stock: e.target.value })} placeholder="∞" /></div>
        <Button onClick={add} disabled={busy || !draft.name.trim()} data-testid="wizard-product-add" className="bg-primary"><Plus className="w-4 h-4 mr-1" /> Añadir</Button>
      </div>
      <div><Label>Descripción (lo que Noah puede decir)</Label><Textarea data-testid="wizard-product-desc" rows={2} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} placeholder="Incluye, beneficios, condiciones…" /></div>
      <div className="space-y-1" data-testid="wizard-product-list">
        {items.map((p) => (
          <div key={p.id} className="flex items-center gap-3 p-2 rounded-2xl bg-muted/40 text-sm" data-testid={`wizard-product-${p.id}`}>
            <Package className="w-4 h-4 text-brand-green shrink-0" />
            <div className="flex-1 min-w-0"><p className="font-medium truncate">{p.name}</p><p className="text-xs text-muted-foreground truncate">{p.description}</p></div>
            <Badge variant="secondary">{p.price != null ? `$${p.price}` : 'sin precio'}</Badge>
            <button onClick={() => remove(p.id)} className="text-muted-foreground hover:text-destructive" data-testid={`wizard-product-remove-${p.id}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        {items.length === 0 && <p className="text-xs text-muted-foreground italic">Todavía no hay productos en esta campaña.</p>}
      </div>
    </Section>
  );
};

const StepCustomer = ({ form, set, setNested }) => (
  <Section title="¿A quién le habla Noah?" hint="Cuanto mejor describas al cliente, mejor califica Noah cada lead.">
    <div><Label>Audiencia objetivo</Label><Input data-testid="campaign-audience-input" value={form.target_audience} onChange={(e) => set({ target_audience: e.target.value })} placeholder="Ej: dueños de pymes en Colombia, 30-50 años" /></div>
    {[['description', 'Quién es', 'Perfil, edad, ciudad, rol…'], ['pains', 'Qué le duele / necesita', 'Problemas que resuelve tu producto'], ['triggers', 'Qué lo motiva a comprar', 'Precio, rapidez, confianza…'], ['objections', 'Qué lo frena', 'Dudas típicas antes de pagar'], ['channels', 'Dónde está', 'Instagram, WhatsApp, referidos…']].map(([k, label, ph]) => (
      <div key={k}><Label>{label}</Label><Textarea data-testid={`customer-profile-${k}`} rows={2} value={form.customer_profile?.[k] || ''} onChange={(e) => setNested('customer_profile', k, e.target.value)} placeholder={ph} /></div>
    ))}
  </Section>
);

const StepStrategy = ({ form, set }) => (
  <Section title="Cómo vende Noah" hint="Pasos, argumentos y manejo de objeciones. Noah sigue este guion sin salirse del contexto.">
    <div><Label>Propuesta de valor</Label><Textarea data-testid="campaign-value-prop" rows={2} value={form.value_proposition} onChange={(e) => set({ value_proposition: e.target.value })} placeholder="¿Por qué elegirte a ti?" /></div>
    <ListField label="Puntos únicos de venta (USPs)" testid="usp" value={form.unique_selling_points} onChange={(v) => set({ unique_selling_points: v })} placeholder="Ej: Entrega en 24h" />
    <ListField label="Pasos del proceso de venta" testid="sales-step" value={form.sales_process_steps} onChange={(v) => set({ sales_process_steps: v })} placeholder="1. Saludar y entender necesidad…" />
    <ListField label="Preguntas para calificar al lead" testid="qualify-q" value={form.lead_qualification_questions} onChange={(v) => set({ lead_qualification_questions: v })} placeholder="¿Para cuándo lo necesitas?" />
    <ListField label="Objeciones frecuentes y cómo responderlas" testid="objection" value={form.common_objections} onChange={(v) => set({ common_objections: v })} placeholder="Es caro → compara con el costo de no resolverlo" />
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div><Label>Cómo hablar de precio</Label><Textarea data-testid="campaign-pricing" rows={2} value={form.pricing_strategy} onChange={(e) => set({ pricing_strategy: e.target.value })} placeholder="Siempre precio con IVA, sin descuentos no autorizados" /></div>
      <div><Label>Promociones vigentes</Label><Textarea data-testid="campaign-promos" rows={2} value={form.promotions} onChange={(e) => set({ promotions: e.target.value })} placeholder="2x1 hasta el 30 de junio" /></div>
    </div>
    <div><Label>Competencia y diferenciadores</Label><Textarea data-testid="campaign-competitors" rows={2} value={form.competitors} onChange={(e) => set({ competitors: e.target.value })} /></div>
    <div><Label>Casos de éxito / testimonios</Label><Textarea data-testid="campaign-success" rows={2} value={form.success_stories} onChange={(e) => set({ success_stories: e.target.value })} /></div>
  </Section>
);

const StepFaq = ({ form, set, campaignId }) => (
  <Section title="Preguntas frecuentes y documentos" hint="Esta es la base de conocimiento. Si la respuesta no está aquí ni en productos/políticas, Noah pregunta y luego escala.">
    <FaqField faqs={form.faqs} onChange={(v) => set({ faqs: v })} />
    {campaignId && <KnowledgeBaseManager campaignId={campaignId} />}
  </Section>
);

const StepPolicies = ({ form, set, setNested }) => (
  <Section title="Políticas oficiales" hint="Noah las cita tal cual. Lo que no esté escrito aquí, no lo promete.">
    {[['shipping', 'Envíos y entregas'], ['returns', 'Cambios y devoluciones'], ['warranty', 'Garantía'], ['payment', 'Pagos y facturación'], ['privacy', 'Privacidad y datos'], ['other', 'Otras políticas']].map(([k, label]) => (
      <div key={k}><Label>{label}</Label><Textarea data-testid={`policy-${k}`} rows={2} value={form.policies?.[k] || ''} onChange={(e) => setNested('policies', k, e.target.value)} /></div>
    ))}
    <ListField label="Métodos de pago aceptados" testid="payment-method" value={form.payment_methods} onChange={(v) => set({ payment_methods: v })} placeholder="Tarjeta, PSE, Nequi…" />
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div><Label>Horario de atención humana</Label><Input data-testid="campaign-hours" value={form.working_hours} onChange={(e) => set({ working_hours: e.target.value })} placeholder="L-V 8am-6pm" /></div>
      <div><Label>Textos legales obligatorios</Label><Input data-testid="campaign-legal" value={form.legal_disclaimers} onChange={(e) => set({ legal_disclaimers: e.target.value })} /></div>
    </div>
  </Section>
);

const StepTone = ({ form, set }) => (
  <Section title="Cómo suena Noah" hint="Personalidad por campaña. El acento manda sobre cualquier otro estilo.">
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div><Label>Acento</Label>
        <Select value={form.accent} onValueChange={(v) => set({ accent: v })}>
          <SelectTrigger data-testid="campaign-accent-select"><SelectValue /></SelectTrigger>
          <SelectContent>{ACCENTS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div><Label>Marca que representa</Label><Input data-testid="campaign-brand" value={form.brand_name} onChange={(e) => set({ brand_name: e.target.value })} /></div>
    </div>
    <div><Label>Tono de voz</Label><Textarea data-testid="campaign-tone" rows={2} value={form.tone_of_voice} onChange={(e) => set({ tone_of_voice: e.target.value })} placeholder="Cercano, directo, sin tecnicismos, tutea…" /></div>
    <ListField label="Mensajes clave a repetir" testid="key-msg" value={form.key_messages} onChange={(v) => set({ key_messages: v })} placeholder="Garantía de 30 días" />
    <ListField label="Temas prohibidos" testid="forbidden" value={form.forbidden_topics} onChange={(v) => set({ forbidden_topics: v })} placeholder="Política, competencia por nombre…" />
  </Section>
);

const StepInstructions = ({ form, set }) => (
  <Section title="Instrucciones para Noah y traspaso a humanos" hint="Reglas específicas del dueño. Tienen prioridad alta en cada respuesta.">
    <div><Label>Instrucciones específicas</Label><Textarea data-testid="campaign-ai-instructions" rows={5} value={form.ai_instructions} onChange={(e) => set({ ai_instructions: e.target.value })} placeholder={'Ej:\n- Pide siempre ciudad antes de hablar de envío.\n- No ofrezcas descuentos; si piden, escala.\n- Cierra proponiendo una llamada.'} /></div>
    <div><Label>Cuándo pasar a un humano</Label><Textarea data-testid="campaign-escalation" rows={2} value={form.escalation_criteria} onChange={(e) => set({ escalation_criteria: e.target.value })} placeholder="Reclamos, pedidos mayores a $5.000, clientes molestos…" /></div>
    <div><Label>Contacto humano</Label><Input data-testid="campaign-contact" value={form.contact_info} onChange={(e) => set({ contact_info: e.target.value })} placeholder="Ana · +57 300 000 0000" /></div>
    <NoahModePicker value={form.noah_mode} onChange={(v) => set({ noah_mode: v })} />
  </Section>
);

export const NoahModePicker = ({ value, onChange }) => (
  <div>
    <Label>Modo de operación de Noah</Label>
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-1">
      {NOAH_MODES.map((m) => (
        <button key={m.value} type="button" onClick={() => onChange(m.value)} data-testid={`noah-mode-${m.value}`}
          className={cn('text-left rounded-2xl border p-3 transition-colors', value === m.value ? 'border-brand-green bg-brand-green/10' : 'border-border hover:bg-muted/40')}>
          <p className="text-sm font-semibold flex items-center gap-1.5">{value === m.value && <CheckCircle2 className="w-4 h-4 text-brand-green" />}{m.title}</p>
          <p className="text-[11px] text-muted-foreground mt-1">{m.desc}</p>
        </button>
      ))}
    </div>
  </div>
);

const StepLaunch = ({ form, set, campaignId }) => {
  const [health, setHealth] = useState(null);
  const [testMsg, setTestMsg] = useState('');
  const [testLog, setTestLog] = useState([]);
  const [testing, setTesting] = useState(false);
  useEffect(() => {
    if (!campaignId) return;
    axios.get(`${API}/api/campaigns/${campaignId}/health`).then(({ data }) => setHealth(data)).catch(() => setHealth(null));
  }, [campaignId, form.noah_mode]);

  const runTest = async () => {
    if (!testMsg.trim()) return;
    setTesting(true);
    const mine = testMsg; setTestMsg('');
    setTestLog((l) => [...l, { who: 'you', text: mine }]);
    try {
      const { data } = await axios.post(`${API}/api/routing/simulate`, { campaign_id: campaignId, platform: 'web', external_id: `wizard-${campaignId.slice(0, 8)}`, sender_name: 'Cliente de prueba', text: mine });
      const n = data.noah || {};
      const text = n.message?.content || n.suggestion?.text || (n.reason === 'manual' ? 'Modo manual: Noah no responde en esta campaña.' : 'Noah no respondió (' + (n.reason || 'sin motivo') + ')');
      setTestLog((l) => [...l, { who: 'noah', text, actions: n.actions || n.suggestion?.actions || [], suggestion: !!n.suggestion }]);
    } catch (e) { toast.error(errorText(e, 'No se pudo probar')); } finally { setTesting(false); }
  };

  const statusColor = useMemo(() => health?.status === 'ready' ? 'text-brand-green' : health?.status === 'almost' ? 'text-brand-copper' : 'text-destructive', [health]);
  return (
    <div className="space-y-5">
      <Section title="Chequeo de salud antes de lanzar" hint="Noah solo debería estar en Automático cuando la base de conocimiento está completa.">
        {health ? (
          <div className="rounded-2xl border border-border p-4 space-y-3" data-testid="campaign-health">
            <div className="flex items-center justify-between">
              <div><p className={cn('text-3xl font-bold', statusColor)} data-testid="campaign-health-score">{health.score}<span className="text-base text-muted-foreground">/100</span></p><p className="text-xs text-muted-foreground">{health.recommendation}</p></div>
              <Badge className={cn('rounded-full', health.ready ? 'bg-brand-green text-white' : 'bg-brand-butter text-brand-deep')}>{health.ready ? 'Lista' : 'Incompleta'}</Badge>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {health.checks.map((c) => (
                <div key={c.key} className="flex items-start gap-2 text-xs p-2 rounded-xl bg-muted/40" data-testid={`health-check-${c.key}`}>
                  {c.ok ? <CheckCircle2 className="w-4 h-4 text-brand-green shrink-0" /> : <AlertCircle className="w-4 h-4 text-brand-copper shrink-0" />}
                  <div><p className="font-medium">{c.label}{c.detail ? <span className="text-muted-foreground"> · {c.detail}</span> : null}</p>{!c.ok && <p className="text-muted-foreground">{c.hint}</p>}</div>
                </div>
              ))}
            </div>
          </div>
        ) : <p className="text-xs text-muted-foreground">Calculando…</p>}
      </Section>
      <NoahModePicker value={form.noah_mode} onChange={(v) => set({ noah_mode: v })} />
      <div className="flex items-center justify-between rounded-2xl border border-border p-3">
        <div><p className="text-sm font-medium">Campaña activa</p><p className="text-xs text-muted-foreground">Si está pausada, los mensajes se guardan pero Noah no responde.</p></div>
        <Switch checked={form.active} onCheckedChange={(v) => set({ active: v })} data-testid="campaign-active-switch" />
      </div>
      <Section title="Prueba a Noah con esta campaña" hint="Simula un cliente. Pasa por el mismo router que WhatsApp o SMS, pero no envía nada fuera.">
        <div className="rounded-2xl border border-border p-3 space-y-2 max-h-56 overflow-y-auto" data-testid="noah-test-log">
          {testLog.length === 0 && <p className="text-xs text-muted-foreground italic">Escribe algo como "Hola, ¿cuánto cuesta?"</p>}
          {testLog.map((m, i) => (
            <div key={i} className={cn('text-sm max-w-[90%] rounded-2xl px-3 py-2 whitespace-pre-wrap', m.who === 'you' ? 'ml-auto bg-brand-deep text-brand-butter' : 'bg-muted')}>
              {m.text}
              {m.suggestion && <p className="text-[10px] mt-1 opacity-70">Modo Copiloto: esto sería una sugerencia, no se envía solo.</p>}
              {m.actions?.length > 0 && <div className="flex flex-wrap gap-1 mt-1.5">{m.actions.map((a, j) => <Badge key={j} variant="outline" className="text-[10px]">{a.action}: {a.value}</Badge>)}</div>}
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <Input data-testid="noah-test-input" value={testMsg} onChange={(e) => setTestMsg(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && runTest()} placeholder="Escribe como cliente…" disabled={testing} />
          <Button onClick={runTest} disabled={testing || !testMsg.trim()} data-testid="noah-test-send" className="bg-primary">{testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</Button>
        </div>
      </Section>
    </div>
  );
};
